import React, { useState, useRef, useEffect } from 'react';
import { UserRound, Trash2, Camera } from 'lucide-react';
import api from '../services/api';
import './ProfilePictureUpload.css';
import '../universal.css';
import UniversalToast from './UniversalToast';

interface ProfilePictureUploadProps {
  currentProfilePicture?: string;
  onProfilePictureUpdate: (newUrl: string | null) => void;
  username: string;
}

const ProfilePictureUpload: React.FC<ProfilePictureUploadProps> = ({
  currentProfilePicture,
  onProfilePictureUpdate,
  username
}) => {
  const [isUploading, setIsUploading] = useState(false);
  const [isRemoving, setIsRemoving] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [showSuccessToast, setShowSuccessToast] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [showErrorToast, setShowErrorToast] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const fileInputRef = useRef<HTMLInputElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function onClickOutside(e: MouseEvent) {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setMenuOpen(false);
      }
    }
    if (menuOpen) {
      document.addEventListener('mousedown', onClickOutside);
    }
    return () => document.removeEventListener('mousedown', onClickOutside);
  }, [menuOpen]);

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
      if (!allowedTypes.includes(file.type)) {
        showErrorToastMessage('Invalid file type. Only JPEG, PNG, GIF, and WebP images are allowed.');
        return;
      }
      const maxSize = 5 * 1024 * 1024; // 5MB
      if (file.size > maxSize) {
        showErrorToastMessage('File too large. Maximum size is 5MB.');
        return;
      }
      
      // Automatically upload the file
      await handleUpload(file);
      setMenuOpen(false);
    }
  };

  const handleOpenFilePicker = () => {
    fileInputRef.current?.click();
  };

  const handleUpload = async (file: File) => {
    setIsUploading(true);
    try {
      const formData = new FormData();
      formData.append('profilePicture', file);

      const response = await api.post('/profile/upload-picture', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      onProfilePictureUpdate(response.data.profile_picture_url);
      showSuccessToastMessage('Profile picture uploaded successfully!');
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    } catch (error: any) {
      const errorMsg = error.response?.data?.error || 'Failed to upload profile picture';
      showErrorToastMessage(errorMsg);
    } finally {
      setIsUploading(false);
    }
  };

  const handleRemove = async () => {
    if (!currentProfilePicture) return;

    setIsRemoving(true);
    try {
      await api.delete('/profile/remove-picture');
      onProfilePictureUpdate(null);
      showSuccessToastMessage('Profile picture removed successfully!');
      setMenuOpen(false);
    } catch (error: any) {
      const errorMsg = error.response?.data?.error || 'Failed to remove profile picture';
      showErrorToastMessage(errorMsg);
    } finally {
      setIsRemoving(false);
    }
  };

  const showErrorToastMessage = (message: string) => {
    setErrorMessage(message);
    setShowErrorToast(true);
    setTimeout(() => setShowErrorToast(false), 4000);
  };

  const showSuccessToastMessage = (message: string) => {
    setSuccessMessage(message);
    setShowSuccessToast(true);
    setTimeout(() => setShowSuccessToast(false), 3000);
  };

  return (
    <div className="profile-picture-upload">
      <div 
        className="profile-picture-container clickable"
        onClick={!isUploading && !isRemoving ? (currentProfilePicture ? () => setMenuOpen((v) => !v) : handleOpenFilePicker) : undefined}
        style={{ cursor: !isUploading && !isRemoving ? 'pointer' : 'default' }}
      >
        {currentProfilePicture ? (
          <img 
            src={currentProfilePicture} 
            alt={`${username}'s profile`}
            className="profile-picture"
          />
        ) : (
          <div className="profile-picture-placeholder">
            <UserRound className="profile-picture-icon" />
          </div>
        )}
        
        {/* Camera overlay for clickable state (no profile picture) */}
        {!currentProfilePicture && (
          <div className="profile-picture-camera-overlay">
            <Camera className="profile-picture-camera-icon" />
          </div>
        )}

        {/* Edit overlay for when user has a profile picture */}
        {currentProfilePicture && (
          <div className="profile-picture-edit-overlay">
            <Camera className="profile-picture-edit-overlay-icon" />
          </div>
        )}
      </div>

      {/* Edit menu - only show when user has a profile picture */}
      {currentProfilePicture && menuOpen && (
        <div className="profile-picture-menu" role="menu" ref={menuRef}>
          <button className="profile-picture-menu-item" role="menuitem" onClick={handleOpenFilePicker} disabled={isUploading || isRemoving}>
            Change photo
          </button>
          <button className="profile-picture-menu-item danger" role="menuitem" onClick={handleRemove} disabled={isRemoving || isUploading}>
            Remove photo
            <Trash2 className="profile-picture-menu-item-icon" />
          </button>
        </div>
      )}

      <input
        ref={fileInputRef}
        type="file"
        accept="image/jpeg,image/jpg,image/png,image/gif,image/webp"
        onChange={handleFileSelect}
        style={{ display: 'none' }}
      />

      {/* Universal Toasts */}
      <UniversalToast type="success" message={successMessage} show={showSuccessToast} />
      <UniversalToast type="error" message={errorMessage} show={showErrorToast} />
    </div>
  );
};

export default ProfilePictureUpload;
